import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Orders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders?userId=${user.uid}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const canCancel = (order) => {
    if (order.status === 'cancelled' || order.status === 'shipped' || order.status === 'delivered') return false;
    const created = new Date(order.createdAt);
    const now = new Date();
    return (now - created) / 3600000 < 12;
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.post(`${API}/orders/${orderId}/cancel`);
      toast.success('Order cancelled');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot cancel order');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <h1 className="text-3xl md:text-4xl font-bold mb-8" style={{fontFamily: 'Playfair Display, serif'}} data-testid="orders-title">My Orders</h1>

      {orders.length === 0 ? (
        <div className="text-center py-12">
          <Package size={64} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500" data-testid="no-orders-message">No orders yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="card p-6" data-testid={`order-${order.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg" data-testid={`order-id-${order.id}`}>Order #{order.orderId}</h3>
                  <p className="text-gray-600 text-sm">{new Date(order.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-3 mt-2 md:mt-0">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`} data-testid={`order-status-${order.id}`}>
                    {order.status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover" />
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-gray-600 text-sm">Qty: {item.quantity} x ₹{item.price}</p>
                    </div>
                    <p className="font-medium">₹{item.price * item.quantity}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-end">
                <div>
                  <p className="text-gray-600 text-sm">Delivery: {order.address}</p>
                  {/* Subtle cancel - only for eligible orders */}
                  {canCancel(order) && (
                    <button
                      onClick={() => handleCancel(order.id)}
                      className="text-xs text-gray-400 hover:text-red-500 mt-2 underline underline-offset-2 transition-colors"
                      data-testid={`cancel-order-${order.id}`}
                    >
                      Cancel order (within 12hrs)
                    </button>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-gray-600 text-sm">Total</p>
                  <p className="text-[#D4AF37] font-bold text-xl" data-testid={`order-total-${order.id}`}>₹{order.total}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
